import type { ICellRendererParams } from 'ag-grid-community';
import { Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PromptHistoryItem, PromptListStatus } from '../types';
import { ModelIcon } from '../components/ModelIcon';
import { PromptTopBrands } from '../components/prompts/PromptTopBrands';
import { ExecutionStatusBadge } from '../components/executions/ExecutionStatusBadge';
import { formatUtcDate } from '@/helpers/formatUtcDate';
import { formatExecutionVisibility } from '../utils/execution-filters';
import { timeAgo } from '../utils/format';

export interface ExecutionsColumnCallbacks {
  onView: (executionId: string) => void;
  onReextract: (promptId: string) => void;
}

export function executionsColumnConfig(callbacks: ExecutionsColumnCallbacks) {
  return [
    {
      field: 'promptText',
      title: 'پرامپت',
      type: 'string',
      flex: 1,
      minWidth: 260,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptHistoryItem>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <span>
            <span className="d-block text-truncate fw-medium text-body">{row.promptText}</span>
            {row.topicName && (
              <span className="d-block fs--2 fw-medium text-primary">{row.topicName}</span>
            )}
          </span>
        );
      },
    },
    {
      field: 'model',
      title: 'مدل',
      type: 'string',
      width: 140,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptHistoryItem>) => {
        const row = params.data;
        if (!row) return null;
        const providerLabel = row.model ?? row.provider ?? '—';
        if (!row.model && !row.provider) return <span>—</span>;
        return (
          <span className="d-flex align-items-center gap-2">
            <ModelIcon svg={row.modelIconSvg} name={providerLabel} size={20} />
            <span className="min-w-0">
              <span className="d-block text-truncate fs--1 fw-medium text-body">
                {providerLabel}
              </span>
              {row.provider && row.model && row.provider !== row.model && (
                <span className="d-block text-truncate fs--2 text-body-tertiary">
                  {row.provider}
                </span>
              )}
            </span>
          </span>
        );
      },
    },
    {
      field: 'visibilityScore',
      title: 'دیده‌شدن در AI',
      type: 'number',
      width: 165,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptHistoryItem>) => {
        const row = params.data;
        if (!row) return null;
        const label = formatExecutionVisibility(row);
        if (label === '—') return <span className="text-500">—</span>;
        const score = Math.min(Math.max(row.visibilityScore, 0), 100);
        return (
          <div className="d-flex align-items-center gap-2 w-100 h-100">
            <div
              className="rounded-pill bg-200 overflow-hidden flex-grow-1"
              style={{ height: '0.375rem', minWidth: '2.5rem' }}
            >
              <div className="h-100 rounded-pill bg-primary" style={{ width: `${score}%` }} />
            </div>
            <span
              className="fw-semibold text-primary flex-shrink-0"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {label}
            </span>
          </div>
        );
      },
    },
    {
      field: 'avgPosition',
      title: 'میانگین رتبه',
      type: 'number',
      width: 140,
      searchable: true,
      valueFormatter: (p: { value: number | null }) => (p.value != null ? String(p.value) : '—'),
    },
    {
      field: 'topBrands',
      title: 'برندها',
      type: 'string',
      width: 150,
      sortable: false,
      searchable: false,
      cellRenderer: (params: ICellRendererParams<PromptHistoryItem>) =>
        params.data ? (
          <PromptTopBrands
            status={
              params.data.status === 'completed' || params.data.status === 'success'
                ? 'executed'
                : (params.data.status as PromptListStatus)
            }
            runs={1}
            brands={params.data.topBrands ?? []}
            brandsFound={params.data.brandsFound}
            max={3}
          />
        ) : null,
    },
    {
      field: 'status',
      title: 'وضعیت',
      type: 'string',
      width: 120,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptHistoryItem>) =>
        params.data ? <ExecutionStatusBadge status={params.data.status} /> : null,
    },
    {
      field: 'executedAt',
      title: 'زمان اجرا',
      type: 'string',
      width: 150,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptHistoryItem>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <span>
            <span className="d-block fs--1 fw-medium text-body">
              {formatUtcDate(row.executedAt, { preset: 'dateTime' }) || '—'}
            </span>
            <span className="d-block fs--2 text-body-tertiary">{timeAgo(row.executedAt)}</span>
          </span>
        );
      },
    },
    {
      field: '_actions',
      title: ' ',
      type: 'string',
      width: 72,
      sortable: false,
      searchable: false,
      noFilter: true,
      pinned: 'left',
      lockPosition: true,
      cellClass: 'over-flow',
      cellStyle: { textAlign: 'center', margin: '0px' },
      cellRenderer: (params: ICellRendererParams<PromptHistoryItem>) => {
        const row = params.data;
        if (!row) return null;
        return (
          <div className="w-100 d-flex h-100 align-items-center justify-content-center">
            <span className="allaho_akbar_zIndex">
              <Dropdown
                onClick={(e) => e.stopPropagation()}
                align="start"
                className="allaho_akbar_zIndex overflow-visible btn-reveal-trigger d-inline-block me-0"
              >
                <Dropdown.Toggle
                  className="text-600 btn-reveal"
                  split
                  variant="reveal"
                  size="sm"
                >
                  <FontAwesomeIcon icon="ellipsis-h" className="fs--2" />
                </Dropdown.Toggle>
                <Dropdown.Menu
                  className="border py-0"
                  popperConfig={{ strategy: 'fixed' }}
                  renderOnMount
                >
                  <Dropdown.Item
                    onClick={() => callbacks.onView(row.id)}
                    className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
                  >
                    <FontAwesomeIcon icon="eye" className="me-1 ms-2" />
                    مشاهده
                  </Dropdown.Item>
                  <Dropdown.Item
                    disabled={
                      !row.promptId ||
                      (row.status !== 'completed' && row.status !== 'success')
                    }
                    onClick={() => {
                      if (row.promptId) callbacks.onReextract(row.promptId);
                    }}
                    className="link-600 fs--1 d-flex align-items-center gap-1 p-0 px-1"
                  >
                    <FontAwesomeIcon icon="redo" className="me-1 ms-2" />
                    استخراج مجدد برندها
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </span>
          </div>
        );
      },
    },
  ];
}
